import { useState, useMemo, useEffect } from 'react'
import { Map, MapControls, MapMarker, MarkerContent, MarkerTooltip, useMap } from '@/components/ui/map'
import { redactStationName } from '../../utils/demoRedaction'
import { sanitizeMapPopupHtml } from '../../utils/sanitizeHtml'
import { filterVehicleGroupKeys, isEntityUlid } from '../../utils/vehicleIdStandardise'
import MapLibreGL from 'maplibre-gl'
import { area } from '@turf/area'

/** Same HeatmapData as GeographicHeatmap - coverage map uses same GPS source. */
interface HeatmapData {
  center: [number, number]
  zoom: number
  station_markers: Array<{ lat: number; lon: number; name: string; popup: string }>
  group_data: {
    [key: string]: {
      Vehicle?: { [id: string]: Array<[number, number, number]> }
      Type?: { [type: string]: Array<[number, number, number]> }
      Station?: { [station: string]: Array<[number, number, number]> }
    } | { [id: string]: Array<[number, number, number]> }
  }
}

/** Backend sends [lat, lon, intensity]. Normalize to [lat, lon] and handle [lon, lat] if present. */
function normalizePoint(p: unknown): [number, number] | null {
  if (!Array.isArray(p) || p.length < 2) return null
  const a = Number(p[0])
  const b = Number(p[1])
  if (!Number.isFinite(a) || !Number.isFinite(b)) return null
  // Lat in [-90, 90], Lon in [-180, 180]. If backend ever sent [lon, lat], swap.
  const looksLikeLatLon = a >= -90 && a <= 90 && b >= -180 && b <= 180
  const looksLikeLonLat = a >= -180 && a <= 180 && b >= -90 && b <= 90
  if (looksLikeLatLon && !looksLikeLonLat) return [a, b]
  if (looksLikeLonLat && !looksLikeLatLon) return [b, a]
  return [a, b]
}

interface GeographicCoverageMapProps {
  data: HeatmapData
  /** When true (demo users), hide real depot/customer names from map popups. */
  redact?: boolean
  /** When true, render only controls + layers (no Map); parent must render a single shared Map. */
  embedInMap?: boolean
  /** When Group By is Vehicle Reg, use this to show customerVehicleName in the Value dropdown */
  getVehicleDisplayName?: (vehicleId: string) => string
  /** When set, show only GPS points for dates in this month (uses YYYY-MM-DD keys in data). */
  selectedMonth?: { year: number; month: number } | null
  /** When set, show only GPS points for this single date (YYYY-MM-DD). Takes precedence over selectedMonth. */
  selectedDate?: string | null
  /** Dates to show in the Date dropdown (YYYY-MM-DD). When length > 0, Date dropdown is shown. */
  availableDates?: string[]
  /** Called when user selects a date from the Date dropdown. */
  onSelectedDateChange?: (date: string | null) => void
  /** Controlled: Group By (Vehicle/Type/Station). When provided, selection stays in sync across map tabs. */
  groupBy?: 'Vehicle' | 'Type' | 'Station'
  /** Controlled: selected value in Group By dropdown. */
  selectedGroupValue?: string | null
  /** Controlled: Show All checkbox. */
  showAll?: boolean
  /** Controlled: Activity (incident/non-operational) for emergency services. */
  selectedActivity?: string
  onGroupByChange?: (v: 'Vehicle' | 'Type' | 'Station') => void
  onSelectedGroupValueChange?: (v: string | null) => void
  onShowAllChange?: (v: boolean) => void
  onSelectedActivityChange?: (v: string) => void
}

const MIN_POINTS_FOR_HULL = 3  // minimum to draw a polygon (convex hull)
const MIN_POINTS_RECOMMENDED = 100  // show warning below this; "Show All" only combines groups with >= this

/**
 * Convex hull using Andrew's monotone chain (matches vehicle_geographic_mapping.py).
 * Input: points as [lon, lat][] (x=lon, y=lat for sort).
 * Output: hull as [lon, lat][] (>= 3 points for a proper polygon).
 */
function convexHullLonLat(points: Array<[number, number]>): Array<[number, number]> {
  if (points.length < 3) return points
  const pts = [...points]
  pts.sort((a, b) => (a[0] === b[0] ? a[1] - b[1] : a[0] - b[0]))
  const cross = (o: [number, number], a: [number, number], b: [number, number]) =>
    (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0])
  const lower: Array<[number, number]> = []
  const upper: Array<[number, number]> = []
  for (let i = 0; i < pts.length; i++) {
    while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], pts[i]) <= 0) lower.pop()
    lower.push(pts[i])
  }
  for (let i = pts.length - 1; i >= 0; i--) {
    while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], pts[i]) <= 0) upper.pop()
    upper.push(pts[i])
  }
  upper.pop()
  lower.pop()
  return lower.concat(upper)
}

/**
 * Compute convex hull from [lat, lon][] points (same data shape as backend).
 * Returns [lat, lon][] for use in component; polygon is drawn in GeoJSON [lon, lat].
 * When points.length < 3, returns points as-is (for bounds fitting only).
 */
function convexHull(points: Array<[number, number]>): Array<[number, number]> {
  if (points.length < 3) return points
  const ptsLonLat = points.map(([lat, lon]) => [lon, lat] as [number, number])
  const hullLonLat = convexHullLonLat(ptsLonLat)
  return hullLonLat.map(([lon, lat]) => [lat, lon] as [number, number])
}

const EARTH_RADIUS_KM = 6371
const KM_TO_MI = 0.621371
const SQ_M_TO_SQ_MI = 1 / 2.589988e6

/** Haversine distance in km between [lat, lon] points (degrees). Matches Python (6371 km). */
function haversineKm(a: [number, number], b: [number, number]): number {
  const [lat1, lon1] = a.map((x) => (x * Math.PI) / 180)
  const [lat2, lon2] = b.map((x) => (x * Math.PI) / 180)
  const dlat = lat2 - lat1
  const dlon = lon2 - lon1
  const x = Math.sin(dlat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dlon / 2) ** 2
  return EARTH_RADIUS_KM * 2 * Math.asin(Math.sqrt(Math.min(1, x)))
}

/**
 * Area of closed polygon (ring as [lat, lon][]) in square miles.
 * Uses Turf geodesic area (m² → mi²). Matches Python HTML "Show All" (turf.area/2_589_988.11).
 * Python single-vehicle uses EPSG:6933 equal-area projection – very close to Turf for typical UK areas.
 */
function polygonAreaSqMi(ring: Array<[number, number]>): number {
  if (ring.length < 3) return 0
  const ringLonLat = ring.map(([lat, lon]) => [lon, lat] as [number, number])
  if (ringLonLat[0]![0] !== ringLonLat[ringLonLat.length - 1]![0] || ringLonLat[0]![1] !== ringLonLat[ringLonLat.length - 1]![1]) {
    ringLonLat.push(ringLonLat[0]!)
  }
  const polygon = { type: 'Polygon' as const, coordinates: [ringLonLat] }
  const areaM2 = area(polygon)
  return Math.max(0, areaM2 * SQ_M_TO_SQ_MI)
}

/**
 * Operational radius: mean and median distance (mi) from each point to the median center.
 * Matches Python calculate_operational_radius: center = np.median(coords), haversine 6371 km, then we convert to mi.
 * Note: Python backend returns radius in km; the Python HTML labels them "mi" without converting (bug). We output true miles.
 */
function operationalRadiusMi(points: Array<[number, number]>): { meanMi: number; medianMi: number } {
  if (points.length === 0) return { meanMi: 0, medianMi: 0 }
  const lats = points.map((p) => p[0])
  const lons = points.map((p) => p[1])
  const center: [number, number] = [
    lats.slice().sort((a, b) => a - b)[Math.floor(lats.length / 2)]!,
    lons.slice().sort((a, b) => a - b)[Math.floor(lons.length / 2)]!,
  ]
  const distsKm = points.map((p) => haversineKm(p, center))
  const distsMi = distsKm.map((d) => d * KM_TO_MI)
  const sorted = distsMi.slice().sort((a, b) => a - b)
  const meanMi = distsMi.reduce((s, d) => s + d, 0) / distsMi.length
  const medianMi = sorted.length % 2 === 1
    ? sorted[(sorted.length - 1) / 2]!
    : (sorted[sorted.length / 2 - 1]! + sorted[sorted.length / 2]!) / 2
  return { meanMi, medianMi }
}

const COVERAGE_SOURCE_ID = 'coverage-source'
const COVERAGE_GLOW_LINE_LAYER_ID = 'coverage-glow-line'
const COVERAGE_LINE_LAYER_ID = 'coverage-line'
const COVERAGE_POINTS_SOURCE_ID = 'coverage-points-source'
const COVERAGE_POINTS_LAYER_ID = 'coverage-points-layer'

function CoverageLayer({
  hullLatLon,
  coveragePointsForDebug,
}: {
  hullLatLon: Array<[number, number]>
  coveragePointsForDebug: Array<[number, number, number]>
}) {
  const { map, isLoaded } = useMap()

  // Remove layers/source on unmount or when polygon/points are cleared
  useEffect(() => {
    if (!map) return
    return () => {
      try {
        if (map.getLayer(COVERAGE_POINTS_LAYER_ID)) map.removeLayer(COVERAGE_POINTS_LAYER_ID)
        if (map.getSource(COVERAGE_POINTS_SOURCE_ID)) map.removeSource(COVERAGE_POINTS_SOURCE_ID)
        if (map.getLayer(COVERAGE_LINE_LAYER_ID)) map.removeLayer(COVERAGE_LINE_LAYER_ID)
        if (map.getLayer(COVERAGE_GLOW_LINE_LAYER_ID)) map.removeLayer(COVERAGE_GLOW_LINE_LAYER_ID)
        if (map.getSource(COVERAGE_SOURCE_ID)) map.removeSource(COVERAGE_SOURCE_ID)
      } catch {
        /* ignore */
      }
    }
  }, [map])

  // Remove polygon when hull has fewer than 3 points (e.g. after selection change)
  useEffect(() => {
    if (!map || hullLatLon.length >= 3) return
    try {
      if (map.getLayer(COVERAGE_LINE_LAYER_ID)) map.removeLayer(COVERAGE_LINE_LAYER_ID)
      if (map.getLayer(COVERAGE_GLOW_LINE_LAYER_ID)) map.removeLayer(COVERAGE_GLOW_LINE_LAYER_ID)
      if (map.getSource(COVERAGE_SOURCE_ID)) map.removeSource(COVERAGE_SOURCE_ID)
    } catch {
      /* ignore */
    }
  }, [map, hullLatLon.length])

  // Add polygon source + layers when map is ready and we have a hull (>= 3 points)
  useEffect(() => {
    if (!isLoaded || !map || hullLatLon.length < 3) return

    const ring = hullLatLon.map(([lat, lon]) => [Number(lon), Number(lat)] as [number, number])
    ring.push(ring[0])
    const polygonGeojson = {
      type: 'FeatureCollection' as const,
      features: [
        {
          type: 'Feature' as const,
          properties: {},
          geometry: { type: 'Polygon' as const, coordinates: [ring] },
        },
      ],
    }

    const applyOrUpdate = () => {
      try {
        if (!map.getSource(COVERAGE_SOURCE_ID)) {
          map.addSource(COVERAGE_SOURCE_ID, { type: 'geojson', data: polygonGeojson })
          // Misty yellow glow (soft blur, low opacity – not a solid line)
          map.addLayer({
            id: COVERAGE_GLOW_LINE_LAYER_ID,
            type: 'line',
            source: COVERAGE_SOURCE_ID,
            paint: {
              'line-color': '#ffff00',
              'line-width': 12,
              'line-blur': 6,
              'line-opacity': 0.35,
            },
          })
          // Red main outline (matches Python: color red, weight 2, opacity 1, fill false)
          map.addLayer({
            id: COVERAGE_LINE_LAYER_ID,
            type: 'line',
            source: COVERAGE_SOURCE_ID,
            paint: {
              'line-color': '#10B981',
              'line-width': 2,
              'line-opacity': 1,
            },
          })
        } else {
          ;(map.getSource(COVERAGE_SOURCE_ID) as MapLibreGL.GeoJSONSource).setData(polygonGeojson)
        }
      } catch (e) {
        console.warn('CoverageLayer: failed to add/update polygon', e)
      }
    }

    const runWhenStyleReady = () => {
      if (map.isStyleLoaded?.()) {
        applyOrUpdate()
        return
      }
      const onIdle = () => { applyOrUpdate() }
      map.once('idle', onIdle)
      const t = setTimeout(() => {
        if (map.isStyleLoaded?.()) applyOrUpdate()
        map.off('idle', onIdle)
      }, 2000)
      return () => {
        clearTimeout(t)
        map.off('idle', onIdle)
      }
    }
    const cancel = runWhenStyleReady()
    return () => { cancel?.() }
  }, [isLoaded, map, hullLatLon])

  // Draw points as circles so coverage is visible (even when polygon cannot be drawn)
  useEffect(() => {
    if (!isLoaded || !map || coveragePointsForDebug.length === 0) return
    const sample = coveragePointsForDebug.length > 500
      ? coveragePointsForDebug.filter((_, i) => i % Math.ceil(coveragePointsForDebug.length / 500) === 0)
      : coveragePointsForDebug
    const pointFeatures: Array<{ type: 'Feature'; properties: Record<string, unknown>; geometry: { type: 'Point'; coordinates: [number, number] } }> = []
    for (const p of sample) {
      const pt = normalizePoint(p)
      if (pt) pointFeatures.push({
        type: 'Feature' as const,
        properties: {},
        geometry: { type: 'Point' as const, coordinates: [pt[1], pt[0]] as [number, number] },
      })
    }
    if (pointFeatures.length === 0) return
    const pointsGeojson = { type: 'FeatureCollection' as const, features: pointFeatures }
    const applyPoints = () => {
      try {
        if (!map.getSource(COVERAGE_POINTS_SOURCE_ID)) {
          map.addSource(COVERAGE_POINTS_SOURCE_ID, { type: 'geojson', data: pointsGeojson })
          map.addLayer({
            id: COVERAGE_POINTS_LAYER_ID,
            type: 'circle',
            source: COVERAGE_POINTS_SOURCE_ID,
            paint: {
              'circle-radius': 3,
              'circle-color': '#22D3EE',
              'circle-opacity': 0.85,
            },
          })
        } else {
          ;(map.getSource(COVERAGE_POINTS_SOURCE_ID) as MapLibreGL.GeoJSONSource).setData(pointsGeojson)
        }
      } catch (e) {
        console.warn('CoverageLayer: failed to add/update points', e)
      }
    }
    const runWhenStyleReady = () => {
      if (map.isStyleLoaded?.()) {
        applyPoints()
        return
      }
      const onIdle = () => { applyPoints() }
      map.once('idle', onIdle)
      const t = setTimeout(() => {
        if (map.isStyleLoaded?.()) applyPoints()
        map.off('idle', onIdle)
      }, 2000)
      return () => {
        clearTimeout(t)
        map.off('idle', onIdle)
      }
    }
    const cancel = runWhenStyleReady()
    return () => { cancel?.() }
  }, [isLoaded, map, coveragePointsForDebug])

  return null
}

function AutoFitCoverage({ hullLatLon }: { hullLatLon: Array<[number, number]> }) {
  const { map, isLoaded } = useMap()
  useEffect(() => {
    if (!isLoaded || !map || hullLatLon.length < 2) return
    const lats = hullLatLon.map(([lat]) => lat)
    const lons = hullLatLon.map(([, lon]) => lon)
    const minLat = Math.min(...lats)
    const maxLat = Math.max(...lats)
    const minLon = Math.min(...lons)
    const maxLon = Math.max(...lons)
    // Add padding so polygon isn't flush with edges (match Python: fitBounds with padding)
    const pad = 0.1
    const latPad = (maxLat - minLat) * pad || 0.01
    const lonPad = (maxLon - minLon) * pad || 0.01
    map.fitBounds(
      [
        [minLon - lonPad, minLat - latPad],
        [maxLon + lonPad, maxLat + latPad],
      ],
      {
        padding: { top: 50, bottom: 50, left: 50, right: 50 },
        duration: 500,
        maxZoom: 15,
      }
    )
  }, [isLoaded, map, hullLatLon])
  return null
}

export function GeographicCoverageMap({ data, redact: _redact = false, embedInMap = false, getVehicleDisplayName, selectedMonth = null, selectedDate = null, availableDates = [], onSelectedDateChange, groupBy: groupByProp, selectedGroupValue: selectedGroupValueProp, showAll: showAllProp, selectedActivity: selectedActivityProp, onGroupByChange, onSelectedGroupValueChange, onShowAllChange, onSelectedActivityChange }: GeographicCoverageMapProps) {
  // Normalize group_data keys (backend may send Vehicle/Type or vehicle/type)
  const groupDataNormalized = useMemo(() => {
    const g = data?.group_data
    if (!g || typeof g !== 'object') return {}
    const out: Record<string, unknown> = {}
    for (const k of Object.keys(g)) {
      const cap = k === 'vehicle' ? 'Vehicle' : k === 'type' ? 'Type' : k === 'station' ? 'Station' : k
      out[cap] = (g as Record<string, unknown>)[k]
    }
    return out
  }, [data?.group_data])

  const hasActivities = useMemo(
    () => 'incident' in groupDataNormalized || 'non-operational' in groupDataNormalized,
    [groupDataNormalized]
  )
  const availableGroupTypes = useMemo(() => {
    const types: Array<'Vehicle' | 'Type' | 'Station'> = ['Vehicle', 'Type']
    if (hasActivities) {
      const act = groupDataNormalized['incident'] || groupDataNormalized['non-operational']
      if (act && typeof act === 'object' && 'Station' in (act as object)) types.push('Station')
    } else if ('Station' in groupDataNormalized) types.push('Station')
    return types
  }, [groupDataNormalized, hasActivities])

  const [selectedActivityInternal, setSelectedActivityInternal] = useState<string>(hasActivities ? 'incident' : '')
  const [groupByInternal, setGroupByInternal] = useState<'Vehicle' | 'Type' | 'Station'>(availableGroupTypes[0] || 'Vehicle')
  const [selectedGroupValueInternal, setSelectedGroupValueInternal] = useState<string | null>(null)
  const [showAllInternal, setShowAllInternal] = useState(false)
  const selectedActivity = selectedActivityProp ?? selectedActivityInternal
  const setSelectedActivity = onSelectedActivityChange ? (v: string) => { setSelectedActivityInternal(v); onSelectedActivityChange(v) } : setSelectedActivityInternal
  const groupBy = groupByProp ?? groupByInternal
  const setGroupBy = onGroupByChange ? (v: 'Vehicle' | 'Type' | 'Station') => { setGroupByInternal(v); onGroupByChange(v) } : setGroupByInternal
  const selectedGroupValue = selectedGroupValueProp !== undefined ? selectedGroupValueProp : selectedGroupValueInternal
  const setSelectedGroupValue = onSelectedGroupValueChange ? (v: string | null) => { setSelectedGroupValueInternal(v); onSelectedGroupValueChange(v) } : setSelectedGroupValueInternal
  const showAll = showAllProp !== undefined ? showAllProp : showAllInternal
  const setShowAll = onShowAllChange ? (v: boolean) => { setShowAllInternal(v); onShowAllChange(v) } : setShowAllInternal
  const mapTheme: 'light' | 'dark' = 'dark'

  const availableGroupValues = useMemo(() => {
    let groupData: { [key: string]: Array<[number, number, number]> } | undefined
    if (hasActivities) {
      const act = groupDataNormalized[selectedActivity]
      if (!act || typeof act !== 'object') return []
      const actObj = act as Record<string, unknown>
      if ('Vehicle' in actObj || 'Type' in actObj || 'Station' in actObj) {
        groupData = (actObj[groupBy] as { [key: string]: Array<[number, number, number]> }) ?? undefined
      }
    } else {
      const directData = groupDataNormalized[groupBy]
      if (directData && typeof directData === 'object') {
        const d = directData as Record<string, unknown>
        if (!('Vehicle' in d && d.Vehicle !== undefined) && !('Type' in d && d.Type !== undefined)) {
          groupData = d as { [key: string]: Array<[number, number, number]> }
        }
      }
    }
    if (!groupData) return []
    return filterVehicleGroupKeys(Object.keys(groupData).sort(), groupBy)
  }, [groupDataNormalized, selectedActivity, groupBy, hasActivities])

  useEffect(() => {
    if (!showAll && availableGroupValues.length > 0) {
      if (!selectedGroupValue || !availableGroupValues.includes(selectedGroupValue)) {
        setSelectedGroupValue(availableGroupValues[0])
      }
    } else if (availableGroupValues.length === 0) setSelectedGroupValue(null)
  }, [availableGroupValues, showAll, selectedGroupValue])

  const coveragePoints = useMemo(() => {
    let groupData: { [key: string]: Array<[number, number, number]> } | undefined
    if (hasActivities) {
      const act = groupDataNormalized[selectedActivity]
      if (!act || typeof act !== 'object') return []
      const actObj = act as Record<string, unknown>
      if ('Vehicle' in actObj || 'Type' in actObj || 'Station' in actObj) {
        groupData = (actObj[groupBy] as { [key: string]: Array<[number, number, number]> }) ?? undefined
      }
    } else {
      const directData = groupDataNormalized[groupBy]
      if (directData && typeof directData === 'object') {
        const d = directData as Record<string, unknown>
        if (!('Vehicle' in d && d.Vehicle !== undefined) && !('Type' in d && d.Type !== undefined)) {
          groupData = d as { [key: string]: Array<[number, number, number]> }
        }
      }
    }
    if (!groupData) return []
    // Resolve date-nested format: { "all": coords, "YYYY-MM-DD": coords }; when selectedDate set use that key; when selectedMonth set use only that month's keys
    const resolvePoints = (entry: unknown): Array<[number, number, number]> => {
      if (Array.isArray(entry)) return entry
      if (!entry || typeof entry !== 'object') return []
      const o = entry as Record<string, unknown>
      if (selectedDate && /^\d{4}-\d{2}-\d{2}$/.test(selectedDate)) {
        const val = o[selectedDate]
        if (Array.isArray(val) && val.length > 0) return val as Array<[number, number, number]>
        // No per-day bucket: fall through to month / all
      }
      if (selectedMonth && selectedMonth.month >= 1 && selectedMonth.month <= 12) {
        const prefix = `${selectedMonth.year}-${String(selectedMonth.month).padStart(2, '0')}-`
        const out: Array<[number, number, number]> = []
        for (const key of Object.keys(o)) {
          if (/^\d{4}-\d{2}-\d{2}$/.test(key) && key.startsWith(prefix)) {
            const val = o[key]
            if (Array.isArray(val)) out.push(...(val as Array<[number, number, number]>))
          }
        }
        if (out.length > 0) return out
      }
      if ('all' in o && Array.isArray(o.all)) return o.all as Array<[number, number, number]>
      return []
    }
    const toPoints = (pts: unknown): Array<[number, number, number]> =>
      resolvePoints(pts).filter((p): p is [number, number, number] => Array.isArray(p) && p.length >= 2)
    if (showAll) {
      const combined = Object.entries(groupData)
        .filter(([key]) => groupBy !== 'Vehicle' || !isEntityUlid(key))
        .filter(([, pts]) => toPoints(pts).length >= MIN_POINTS_RECOMMENDED)
        .flatMap(([, pts]) => toPoints(pts))
      return combined
    }
    if (selectedGroupValue && groupData[selectedGroupValue]) {
      const pts = toPoints(groupData[selectedGroupValue])
      return pts
    }
    return []
  }, [groupDataNormalized, selectedActivity, groupBy, selectedGroupValue, showAll, hasActivities, selectedMonth, selectedDate])

  const hullLatLon = useMemo(() => {
    if (coveragePoints.length < 3) return []
    const pts: Array<[number, number]> = []
    for (const p of coveragePoints) {
      const pt = normalizePoint(p)
      if (pt) pts.push(pt)
    }
    if (pts.length < 3) return []
    const sampled = pts.length > 10000 ? pts.filter((_, i) => i % 3 === 0) : pts
    return convexHull(sampled)
  }, [coveragePoints])

  const setGroupByAndFirst = (newGroupBy: 'Vehicle' | 'Type' | 'Station') => {
    setGroupBy(newGroupBy)
    let groupData: { [key: string]: Array<[number, number, number]> } | undefined
    if (hasActivities) {
      const act = groupDataNormalized[selectedActivity]
      if (act && typeof act === 'object') {
        const actObj = act as Record<string, unknown>
        if ('Vehicle' in actObj || 'Type' in actObj)
          groupData = (actObj[newGroupBy] as { [key: string]: Array<[number, number, number]> }) ?? undefined
      }
    } else {
      const directData = groupDataNormalized[newGroupBy]
      if (directData && typeof directData === 'object') {
        const d = directData as Record<string, unknown>
        if (!('Vehicle' in d && d.Vehicle !== undefined) && !('Type' in d && d.Type !== undefined))
          groupData = d as { [key: string]: Array<[number, number, number]> }
      }
    }
    const keys = groupData ? Object.keys(groupData).sort() : []
    setSelectedGroupValue(keys.length > 0 ? keys[0] : null)
  }

  const noData = coveragePoints.length === 0
  const hasInsufficientData = coveragePoints.length > 0 && coveragePoints.length < MIN_POINTS_RECOMMENDED
  const noDataReason = showAll
    ? `No vehicles meet minimum of ${MIN_POINTS_RECOMMENDED} points.`
    : `Select a value with at least ${MIN_POINTS_FOR_HULL} points for polygon.`

  // Bounds for auto-fit: use hull if >= 2 points, else use normalized coverage points
  const fitBoundsPoints = useMemo(() => {
    if (hullLatLon.length >= 2) return hullLatLon
    const pts: Array<[number, number]> = []
    for (const p of coveragePoints) {
      const pt = normalizePoint(p)
      if (pt) pts.push(pt)
    }
    return pts
  }, [hullLatLon, coveragePoints])

  // Service area (convex hull) in mi² and operational radius (mean/median in mi).
  // Area: Turf geodesic (matches Python HTML "Show All"); Python single-vehicle uses EPSG:6933 (very close).
  // Radius: same logic as Python (median center, haversine); we convert km→mi; Python HTML shows km as "mi".
  const coverageStats = useMemo(() => {
    const pts: Array<[number, number]> = []
    for (const p of coveragePoints) {
      const pt = normalizePoint(p)
      if (pt) pts.push(pt)
    }
    if (pts.length < 3) return null
    const areaSqMi = polygonAreaSqMi(hullLatLon.length >= 3 ? hullLatLon : pts)
    const { meanMi, medianMi } = operationalRadiusMi(pts)
    return { serviceAreaSqMi: areaSqMi, radiusMeanMi: meanMi, radiusMedianMi: medianMi }
  }, [coveragePoints, hullLatLon])

  // When redaction is enabled (demo users), avoid exposing real depot/customer names in station popups.
  const stationMarkers = useMemo(() => {
    const markers = Array.isArray(data.station_markers) ? data.station_markers : []
    if (!_redact) return markers
    return markers.map((marker, idx) => ({
      ...marker,
      popup: `Station ${String.fromCharCode(65 + (idx % 26))}`,
    }))
  }, [data.station_markers, _redact])

  return (
    <div className="relative w-full h-full pointer-events-none">
      <div className="absolute top-4 left-4 z-10 bg-card p-4 rounded-lg shadow-lg border border-border min-w-70 pointer-events-auto">
        <h4 className="font-bold mb-3 text-white text-center">Coverage Map</h4>
        <div className="space-y-3">
          {availableDates.length > 0 && onSelectedDateChange && (
            <div>
              <label className="block text-sm font-medium mb-1 text-foreground">Date:</label>
              <select
                value={selectedDate ?? ''}
                onChange={(e) => onSelectedDateChange(e.target.value || null)}
                className="w-full p-2 border border-border rounded bg-input text-white text-sm"
              >
                <option value="">All dates in period</option>
                {availableDates.map((d) => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
            </div>
          )}
          {hasActivities && (
            <div>
              <label className="block text-sm font-medium mb-1 text-foreground">Activity:</label>
              <select
                value={selectedActivity}
                onChange={(e) => setSelectedActivity(e.target.value)}
                className="w-full p-2 border border-border rounded bg-input text-white text-sm"
              >
                <option value="incident">Incident</option>
                <option value="non-operational">Non-Operational</option>
              </select>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium mb-1 text-foreground">Group By:</label>
            <select
              value={groupBy}
              onChange={(e) => setGroupByAndFirst(e.target.value as 'Vehicle' | 'Type' | 'Station')}
              className="w-full p-2 border border-border rounded bg-input text-white text-sm"
            >
              <option value="Vehicle">Vehicle Reg</option>
              <option value="Type">Vehicle Type</option>
              {availableGroupTypes.includes('Station') && <option value="Station">Station</option>}
            </select>
          </div>
          {!showAll && (
            <div>
              <label className="block text-sm font-medium mb-1 text-foreground">Value:</label>
              <select
                value={selectedGroupValue || ''}
                onChange={(e) => setSelectedGroupValue(e.target.value || null)}
                className="w-full p-2 border border-border rounded bg-input text-white text-sm"
              >
                <option value="">Select...</option>
                {availableGroupValues.map((k) => {
                  const label =
                    groupBy === 'Vehicle' && getVehicleDisplayName
                      ? getVehicleDisplayName(k)
                      : groupBy === 'Station'
                        ? redactStationName(k, _redact ?? false)
                        : k
                  return (
                    <option key={k} value={k}>{label}</option>
                  )
                })}
              </select>
            </div>
          )}
          <div>
            <label className="flex items-center text-sm text-foreground">
              <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} className="mr-2" />
              Show All
            </label>
          </div>
          {coverageStats && (
            <div className="text-xs pt-2 border-t border-border text-muted-foreground space-y-1">
              <div className="font-medium text-foreground">Service Area: {coverageStats.serviceAreaSqMi.toFixed(2)} mi²</div>
              <div className="font-medium text-foreground">Operational Radius:</div>
              <div className="pl-2">Mean: {coverageStats.radiusMeanMi.toFixed(2)} mi</div>
              <div className="pl-2">Median: {coverageStats.radiusMedianMi.toFixed(2)} mi</div>
            </div>
          )}
          {coveragePoints.length > 0 && (
            <div className="text-xs pt-2 border-t border-border text-muted-foreground">
              <div>Points: {coveragePoints.length.toLocaleString()}</div>
            </div>
          )}
          {noData && (
            <div className="text-warning text-xs">{noDataReason}</div>
          )}
          {hasInsufficientData && (
            <div className="text-warning text-xs">
              ⚠️ Under {MIN_POINTS_RECOMMENDED} points; coverage area may be approximate.
            </div>
          )}
        </div>
      </div>
      {embedInMap ? (
        <>
          {coveragePoints.length > 0 && (
            <>
              <CoverageLayer
                hullLatLon={hullLatLon}
                coveragePointsForDebug={coveragePoints}
              />
              <AutoFitCoverage hullLatLon={fitBoundsPoints} />
            </>
          )}
        </>
      ) : (
        <div className="w-full h-full">
          <Map center={[data.center[1], data.center[0]]} zoom={data.zoom} theme={mapTheme}>
            <MapControls position="bottom-right" showZoom showCompass showLocate showFullscreen />
            {stationMarkers.map((marker, idx) => (
              <MapMarker key={idx} longitude={marker.lon} latitude={marker.lat}>
                <MarkerContent>
                  <div className="relative cursor-pointer">
                    <svg width="36" height="48" viewBox="0 0 24 32" fill="none" xmlns="http://www.w3.org/2000/svg" className="drop-shadow-lg" style={{ filter: 'drop-shadow(0 0 3px rgba(255,255,255,0.8))' }}>
                      <path d="M12 0C7.58 0 4 3.58 4 8C4 14 12 24 12 24C12 24 20 14 20 8C20 3.58 16.42 0 12 0Z" fill="#F59E0B" stroke="#FFFFFF" strokeWidth="1.5" />
                      <rect x="7" y="10" width="10" height="8" fill="#FFFFFF" stroke="#F59E0B" strokeWidth="0.5" />
                      <rect x="8" y="12" width="2" height="2" fill="#F59E0B" />
                      <rect x="11" y="12" width="2" height="2" fill="#F59E0B" />
                      <rect x="14" y="12" width="2" height="2" fill="#F59E0B" />
                      <line x1="7" y1="14" x2="17" y2="14" stroke="#F59E0B" strokeWidth="0.5" />
                      <polygon points="9,10 12,7 15,10" fill="#FFFFFF" />
                    </svg>
                  </div>
                </MarkerContent>
                <MarkerTooltip className="!p-0 !m-0 bg-background text-white rounded shadow-lg">
                  <div className="px-2 py-1 text-xs" dangerouslySetInnerHTML={{ __html: sanitizeMapPopupHtml(marker.popup) }} />
                </MarkerTooltip>
              </MapMarker>
            ))}
            {coveragePoints.length > 0 && (
              <>
                <CoverageLayer
                  hullLatLon={hullLatLon}
                  coveragePointsForDebug={coveragePoints}
                />
                <AutoFitCoverage hullLatLon={fitBoundsPoints} />
              </>
            )}
          </Map>
        </div>
      )}
    </div>
  )
}
